import { cva } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';

const pageBtn = cva(
  'flex items-center justify-center rounded-md border-none transition-colors font-medium whitespace-nowrap cursor-pointer',
  {
    variants: {
      intent: {
        active: 'bg-brand text-brand-foreground font-semibold',
        default:
          'bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
        arrow:
          'bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:pointer-events-none',
      },
      size: {
        md: 'h-8 min-w-8 px-2 text-sm',
        sm: 'h-7 min-w-7 px-2 text-xs',
      },
    },
    defaultVariants: { intent: 'default', size: 'md' },
  },
);

type Props = {
  page: number;
  total: number;
  size: 'sm' | 'md';
  compact: boolean;
  onPageChange: (page: number) => void;
};

const PaginationButtons = ({
  page,
  total,
  size,
  compact,
  onPageChange,
}: Props) => {
  const { t } = useTranslation('misc', { keyPrefix: 'pagination' });

  const getPageNumbers = () => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages: (number | '…')[] = [1];
    if (page > 2) pages.push('…');

    const start = Math.max(2, page - (compact ? 0 : 1));
    const end = Math.min(total - 1, page + (compact ? 0 : 1));
    for (let i = start; i <= end; i++) pages.push(i);

    if (page < total - (compact ? 1 : 2)) pages.push('…');
    pages.push(total);
    return pages;
  };

  return (
    <nav aria-label="Pagination" className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onPageChange(page - 1)}
        disabled={page === 1}
        aria-label={t('previousPage')}
        className={pageBtn({ intent: 'arrow', size })}
      >
        <i className="fa-solid fa-chevron-left text-xs" />
      </button>

      <div className="flex items-center gap-1">
        {getPageNumbers().map((p, i) =>
          p === '…' ? (
            <span
              key={`ellipsis-${i}`}
              className="flex items-center justify-center h-7 w-7 text-xs text-muted-foreground/60"
            >
              ···
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              aria-current={p === page ? 'page' : undefined}
              className={pageBtn({
                intent: p === page ? 'active' : 'default',
                size,
              })}
            >
              {p}
            </button>
          ),
        )}
      </div>

      <button
        type="button"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= total}
        aria-label={t('nextPage')}
        className={pageBtn({ intent: 'arrow', size })}
      >
        <i className="fa-solid fa-chevron-right text-xs" />
      </button>
    </nav>
  );
};

export default PaginationButtons;
